'use client';

import { useState, useEffect } from 'react';
import {
  Sparkles,
  Server,
  Map,
  Target,
  BookOpen,
  Undo2,
  Copy
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AskAIBar } from '@/components/ai';
import { VersionsDropdown, type Version } from '@/components/versions';
import { DeliveryTemplate } from './templates';
import { ArtifactGeneratorInput, type AttachedFile } from '@/components/shared/artifact-generator-input';
import { useToast } from '@/hooks/use-toast';
import { useAuthToken } from '@/hooks/use-auth';
import { useQueryClient } from '@tanstack/react-query';
import { useDeliveryVersions, useDeliveryArtifact, extractDeliveryMarkdown, type DeliveryType } from '@/hooks/use-delivery';

type DeliveryView = 'architecture' | 'storymap' | 'mvp' | 'stories';

interface DeliveryConfig {
  id: DeliveryView;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  emptyTitle: string;
  emptyDescription: string;
  placeholder: string;
  askAIPlaceholder: string;
}

const deliveryConfigs: Record<DeliveryView, DeliveryConfig> = {
  architecture: {
    id: 'architecture',
    title: 'Technical Architecture',
    subtitle: 'Technical architecture for',
    icon: Server,
    emptyTitle: 'Define Technical Architecture',
    emptyDescription: 'Generate comprehensive technical architecture by describing system requirements, technology stack, and infrastructure needs, or upload existing documentation.',
    placeholder: "Describe your technical requirements... (e.g., 'Cloud-native microservices, Kubernetes deployment, CI/CD pipeline')",
    askAIPlaceholder: "E.g., Add disaster recovery, focus on security, include monitoring stack...",
  },
  storymap: {
    id: 'storymap',
    title: 'Story Map',
    subtitle: 'Story map for',
    icon: Map,
    emptyTitle: 'Create Story Map',
    emptyDescription: 'Generate a visual story map by describing user activities, tasks, and stories, or upload existing product backlog and user research.',
    placeholder: "Describe user activities and stories... (e.g., 'User registration flow, onboarding journey, checkout process')",
    askAIPlaceholder: "E.g., Add release slices, focus on MVP scope, include dependencies...",
  },
  mvp: {
    id: 'mvp',
    title: 'MVP Scope',
    subtitle: 'MVP scope for',
    icon: Target,
    emptyTitle: 'Define MVP Scope',
    emptyDescription: 'Generate MVP scope definition by describing core features, success criteria, and constraints, or upload product requirements documents.',
    placeholder: "Describe your MVP requirements... (e.g., 'Core authentication, basic dashboard, essential reporting features')",
    askAIPlaceholder: "E.g., Add success metrics, focus on must-have features, include constraints...",
  },
  stories: {
    id: 'stories',
    title: 'Stories',
    subtitle: 'User stories for',
    icon: BookOpen,
    emptyTitle: 'Generate User Stories',
    emptyDescription: 'Generate detailed user stories by describing user goals and acceptance criteria, or upload requirements documents and feature specifications.',
    placeholder: "Describe user goals and features... (e.g., 'As a user, I want to view my dashboard so I can track my progress')",
    askAIPlaceholder: "E.g., Add acceptance criteria, focus on edge cases, include technical stories...",
  },
};

interface DeliveryPanelProps {
  projectId?: string;
  projectName?: string;
  activeView?: DeliveryView;
  onViewChange?: (view: DeliveryView) => void;
  onAskAI?: (message: string) => void;
  onVersionSelect?: (version: Version) => void;
  onUndo?: () => void;
  onCopy?: () => void;
  onContentGenerated?: (content: string) => void;
}

export function DeliveryPanel({
  projectId,
  projectName = 'Enterprise Banking Platform',
  activeView = 'architecture',
  onViewChange,
  onAskAI,
  onVersionSelect,
  onUndo,
  onCopy,
  onContentGenerated,
}: DeliveryPanelProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAskAI, setShowAskAI] = useState(false);
  const [contentByView, setContentByView] = useState<Record<string, string>>({});
  const [showGenerateFormByView, setShowGenerateFormByView] = useState<Record<string, boolean>>({});
  const [isResetting, setIsResetting] = useState(false);
  const { toast } = useToast();
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const artifactType = activeView as DeliveryType;

  // Fetch persisted artifact for current view (enables content after reload)
  const { data: artifact } = useDeliveryArtifact(
    projectId,
    artifactType
  );

  // Hydrate contentByView from persisted artifact when it loads
  useEffect(() => {
    const markdown = artifact ? extractDeliveryMarkdown(artifact.content) : null;
    if (markdown && projectId) {
      setContentByView(prev => ({ ...prev, [activeView]: markdown }));
    }
  }, [artifact, activeView, projectId]);

  // Prefer local state, fall back to artifact (ensures document shows on reload before useEffect runs)
  const artifactMarkdown = artifact ? extractDeliveryMarkdown(artifact.content) : null;
  const generatedContent = contentByView[activeView] ?? artifactMarkdown ?? null;
  const showForm = showGenerateFormByView[activeView] ?? false;
  const setGeneratedContent = (content: string | null) => {
    setContentByView(prev => {
      if (content === null) {
        const next = { ...prev };
        delete next[activeView];
        return next;
      }
      return { ...prev, [activeView]: content };
    });
  };

  // Fetch versions from API
  const deliveryType = activeView as DeliveryType;
  const { data: versions = [] } = useDeliveryVersions(
    projectId,
    deliveryType
  );

  const config = deliveryConfigs[activeView];
  const Icon = config.icon;

  const handleGenerate = async (description: string, attachedFiles: AttachedFile[]) => {
    if (!projectId) {
      toast({
        title: 'Error',
        description: 'Project ID is required',
        variant: 'destructive',
      });
      return;
    }

    if (!token) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to generate content.',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);
    try {
      const fileContext = attachedFiles.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size,
      }));

      const deliveryType = activeView;
      const response = await fetch(`/api/projects/${projectId}/delivery/${deliveryType}/generate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: description,
          attachedFiles: fileContext,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Backend returns { artifact: { content: { markdown: ... } } }
        const content = data.data.artifact?.content || data.data.content;
        // Extract markdown from content object, or use content directly if it's a string
        let markdownContent: string;
        if (typeof content === 'object' && content !== null) {
          // New format: { markdown: "...", generatedAt: "...", model: "..." }
          markdownContent = content.markdown || content.content || JSON.stringify(content, null, 2);
        } else {
          markdownContent = String(content || '');
        }
        setGeneratedContent(markdownContent);
        setShowGenerateFormByView(prev => ({ ...prev, [activeView]: false }));
        onContentGenerated?.(markdownContent);
        if (projectId) {
          queryClient.invalidateQueries({ queryKey: ['delivery', projectId, activeView, 'artifact'] });
          queryClient.invalidateQueries({ queryKey: ['delivery', projectId, 'list'] });
        }
        toast({
          title: 'Content Generated',
          description: 'AI has generated your content successfully.',
        });
      } else {
        throw new Error(data.message || 'Failed to generate content');
      }
    } catch (error) {
      toast({
        title: 'Generation Failed',
        description: error instanceof Error ? error.message : 'Failed to generate content',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReset = async () => {
    if (!projectId || !token) return;
    setIsResetting(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/delivery/${activeView}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error?.message || 'Failed to delete document');
      }
      setGeneratedContent(null);
      setShowGenerateFormByView(prev => ({ ...prev, [activeView]: true }));
      queryClient.invalidateQueries({ queryKey: ['delivery', projectId, activeView, 'artifact'] });
      queryClient.invalidateQueries({ queryKey: ['delivery', projectId, activeView, 'versions'] });
      queryClient.invalidateQueries({ queryKey: ['delivery', projectId, 'list'] });
      toast({ title: 'Document Reset', description: 'Document deleted. You can generate a new one.' });
    } catch (error) {
      toast({
        title: 'Reset Failed',
        description: error instanceof Error ? error.message : 'Failed to delete document',
        variant: 'destructive',
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleAskAI = async (message: string) => {
    await onAskAI?.(message);
    toast({
      title: "AI Request Sent",
      description: "Your request is being processed...",
    });
  };

  const handleVersionSelect = (version: Version) => {
    onVersionSelect?.(version);
  };

  const handleUndo = async () => {
    if (generatedContent && !showForm) {
      await handleReset();
    } else {
      onUndo?.();
      toast({
        title: "Undo",
        description: "Last action undone",
      });
    }
  };

  const handleCopy = () => {
    if (generatedContent) {
      navigator.clipboard.writeText(generatedContent);
    }
    onCopy?.();
    toast({
      title: "Copied",
      description: "Content copied to clipboard",
    });
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* FIXED HEADER - Does NOT scroll */}
      <div className="shrink-0 bg-white px-8 py-6 border-b border-gray-200">
        <div className="flex items-start justify-between">
          {/* Title */}
          <div>
            <h1 className="text-2xl font-medium text-gray-900 tracking-tight">
              {config.title}
            </h1>
            <p className="text-base text-gray-600 mt-1">
              {config.subtitle} {projectName}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Ask AI Button */}
            <button
              onClick={() => setShowAskAI(!showAskAI)}
              className={cn(
                "h-[42px] px-4 flex items-center gap-2 rounded-[10px] transition-colors text-white",
                showAskAI 
                  ? "bg-brand-primary/80" 
                  : "bg-brand-primary hover:bg-brand-primary/90"
              )}
            >
              <Sparkles className="w-4 h-4" />
              <span className="text-base">Ask AI</span>
            </button>

            {/* Versions Dropdown */}
            <VersionsDropdown
              versions={versions}
              onVersionSelect={handleVersionSelect}
            />

            {/* Undo Button - only show when document is displayed */}
            {generatedContent && !showForm && (
              <button 
                onClick={handleUndo}
                disabled={isResetting}
                className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-colors text-gray-700 disabled:opacity-50"
              >
                <Undo2 className="w-4 h-4" />
                <span className="text-base">{isResetting ? 'Resetting...' : 'Undo'}</span>
              </button>
            )}

            {/* Copy Button - only show when document is displayed */}
            {generatedContent && !showForm && (
              <button 
                onClick={handleCopy}
                className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-colors text-gray-700"
              >
                <Copy className="w-4 h-4" />
                <span className="text-base">Copy</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="container">
        {/* Ask AI Bar */}
        {showAskAI && (
          <div className="px-8 pt-6">
            <AskAIBar
              isOpen={showAskAI}
              onClose={() => setShowAskAI(false)}
              onSend={handleAskAI}
              placeholder={config.askAIPlaceholder}
            />
          </div>
        )}

        {/* Content Area */}
        {generatedContent && !showForm ? (
          <div className="px-8 py-8">
            <DeliveryTemplate markdown={generatedContent} activeView={activeView} />
          </div>
        ) : (
          /* Empty State or Generate New Form */
          <div className="px-8 py-8">
            <div className="max-w-[585px] mx-auto">
              {/* Icon and Title */}
              <div className="text-center mb-8">
                {/* Icon */}
                <div className="w-20 h-20 mx-auto mb-6 bg-[#e0e7ff] rounded-[14px] flex items-center justify-center">
                  <Icon className="w-10 h-10 text-brand-primary" />
                </div>

                <h2 className="text-lg font-medium text-gray-900 mb-3">
                  {config.emptyTitle}
                </h2>

                <p className="text-base text-gray-600 max-w-[448px] mx-auto leading-6">
                  {config.emptyDescription}
                </p>
              </div>

              {/* Input Area with File Attachment */}
              <ArtifactGeneratorInput
                key={activeView}
                placeholder={config.placeholder}
                onGenerate={handleGenerate}
                isGenerating={isGenerating}
              />

              {/* Footer Text */}
              <p className="text-center text-base text-gray-500 mt-6">
                AI will analyze your input and generate comprehensive content
              </p>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

export type { DeliveryView };
